import React, { useEffect, useMemo, useRef, useState } from "react";
import { addStateEntry, itemsState, uiState, updateTaskCounts } from "@/states.ts";
import { useSnapshot } from "valtio/react";
import { createItem, getItems, updateListItemsSort } from "@/api/offline.ts";
import { processQueue } from "@/api/queue.ts";
import { ItemView } from "./ItemView.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { ItemsNavBar } from "@/views/items/ItemsNavBar.tsx";
import { EditListMenu } from "@/blocks/EditListMenu.tsx";
import { useList } from "@/hooks/lists.ts";
import {
	closestCenter,
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	TouchSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortEntry } from "@/api/api.ts";

export interface ItemsViewProps {
	listId: string;
}

const ItemsView: React.FC<ItemsViewProps> = (props) => {
	const [title, setTitle] = useState("");

	const { listId } = props;
	const { items } = useSnapshot(itemsState);
	const list = useList(props.listId);
	const isMobile = useIsMobile();
	const inputRef = useRef<HTMLInputElement | null>(null);
	// TODO errors

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(TouchSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	useEffect(() => {
		uiState.editItems = false;
		getItems(listId);
	}, [listId]);

	const handleCreateItem = (e: React.SyntheticEvent) => {
		e.preventDefault();
		if (!title) {
			return;
		}

		const count = itemsState.items[listId]?.length || 0;

		createItem(listId, title, count + 1).then((l) => {
			if (!itemsState.items[listId]) {
				itemsState.items[listId] = [];
			}
			addStateEntry(itemsState.items[listId], l.item, true);
			updateTaskCounts(listId);
			setTitle("");
			if (!isMobile) {
				inputRef.current?.focus();
			}
		});
		processQueue();
	};

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;

		if (!active || !over) {
			return;
		}

		if (active.id !== over.id) {
			const newItems = [...sortedItems];
			const oldIndex = sortedItems.findIndex((i) => i.id === active.id);
			const newIndex = sortedItems.findIndex((i) => i.id === over.id);
			const newSorted = arrayMove(newItems, oldIndex, newIndex);
			const newSortedEntries = newSorted.reverse().map((e, i) => ({
				id: e.id,
				order: i,
			})) as SortEntry[];
			updateListItemsSort(listId, newSortedEntries).then(() => {
				itemsState.items[listId] = itemsState.items[listId].map((l) => {
					const newSortedEntry = newSortedEntries.find(
						(e) => e.id === l.id,
					);
					if (newSortedEntry) {
						l.order = newSortedEntry.order;
					}
					return l;
				});
			});
			processQueue();
		}
	};

	const listItems = items[listId] || [];

	const sortedItems = useMemo(() => {
		if (!listItems) {
			return [];
		}
		return [...listItems].sort((a, b) => {
			// Sort by completed status: uncompleted (-1) comes before completed (1)
			if (!a.completed && b.completed) {
				return -1;
			}
			if (a.completed && !b.completed) {
				return 1;
			}
			// If both have the same completed status, sort by order
			return (b.order || 0) - (a.order || 0);
		});
	}, [listItems]);

	return (
		<div className={""}>
			{isMobile && <ItemsNavBar />}
			<div className={"flex items-center justify-between mb-5"}>
				<div className={"flex items-center gap-4"}>
					<div className={"text-2xl"}>{list?.title}</div>
				</div>
				<EditListMenu />
			</div>
			<form target={"#"} onSubmit={handleCreateItem}>
				<div className={"flex gap-2 mb-8"}>
					<Input
						ref={inputRef}
						placeholder={"New Task..."}
						type="text"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
					/>
					<Button onClick={handleCreateItem}>Add</Button>
				</div>
			</form>
			{
				<div>
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
					>
						<SortableContext
							items={sortedItems}
							strategy={verticalListSortingStrategy}
						>
							{sortedItems.map((l) => (
								<ItemView
									listId={listId}
									key={l.id}
									item={l}
								/>
							))}
						</SortableContext>
					</DndContext>
				</div>
			}
		</div>
	);
};

export { ItemsView };
